/**
 * src/hooks/useVoice.ts
 * -----------------------
 * React hook for the full Voice-to-Voice pipeline.
 *
 * startSession()
 *   - Ensures an active session exists (creates an anonymous one if /voice
 *     was opened directly), then opens a WebSocket to /api/v1/voice/{sessionId}
 *   - Starts microphone capture (PCM16 @ 24 kHz) via audio.ts
 *   - Streams raw PCM16 binary frames to the backend in real-time
 *
 * Incoming WebSocket messages:
 *   transcript           → voiceStore.appendTranscript
 *   state_change         → voiceStore.setState (clears interruptedRef)
 *   response_audio_chunk → feed PCMPlayer (dropped if interruptedRef is set)
 *   audio_interrupted    → stop PCMPlayer immediately, set interruptedRef
 *   error                → voiceStore.setState('idle')
 *
 * endSession()   — closes WS (safe in any ready-state), stops mic, destroys player
 * toggleMute()   — enable/disable mic track without closing socket
 *
 * A monotonically-increasing token guards against React 18 StrictMode's
 * mount→unmount→mount double-invoke (and rapid route changes): any async
 * startup work checks the token and aborts if it has been superseded.
 */
import { useCallback, useRef } from 'react'
import { useVoiceStore } from '@/stores/voice'
import { useSessionStore } from '@/stores/session'
import { useAuthStore } from '@/stores/auth'
import { createSession as apiCreateSession } from '@/api/sessions'
import { PCMPlayer, startRecording } from '@/lib/audio'
import { getCurrentOrgId } from '@/lib/org'
import i18n from '@/lib/i18n'
import type { RecordingHandle } from '@/lib/audio'
import type { VoiceWsMessage } from '@/types/voice'

export function useVoice() {
  const voiceStore = useVoiceStore()
  const wsRef = useRef<WebSocket | null>(null)
  const recordingRef = useRef<RecordingHandle | null>(null)
  const playerRef = useRef<PCMPlayer | null>(null)
  const interruptedRef = useRef(false)
  const tokenRef = useRef(0)

  const startSession = useCallback(async () => {
    const token = ++tokenRef.current
    voiceStore.resetSession()
    voiceStore.setState('idle')

    // Ensure a session exists — /voice may be opened directly (no chat first).
    const orgId = getCurrentOrgId()
    const sessionStore = useSessionStore.getState()
    const authStore = useAuthStore.getState()
    let sid = sessionStore.activeSessionId
    const activeSession = sessionStore.sessions.find((session) => session.id === sid)
    const needsAuthenticatedSession = authStore.isAuthenticated && activeSession?.isAnonymous
    const belongsToSignedInUser = !authStore.isAuthenticated || !activeSession?.userId || activeSession.userId === authStore.user?.id
    if (!sid || !activeSession || activeSession.orgId !== orgId || needsAuthenticatedSession || !belongsToSignedInUser) {
      try {
        const session = await apiCreateSession(orgId)
        if (tokenRef.current !== token) return // superseded (unmounted / re-mounted)
        useSessionStore.getState().prependSession(session)
        useSessionStore.getState().setActiveSession(session.id)
        sid = session.id
      } catch {
        if (tokenRef.current === token) voiceStore.setState('idle')
        return
      }
    }
    if (tokenRef.current !== token) return

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const params = new URLSearchParams({ org_id: orgId })
    if (authStore.accessToken) params.set('token', authStore.accessToken)
    // Tell the backend which language this customer is actually using. Without
    // it the transcription model auto-detects from the audio alone, and short
    // or noisy English clips came back as Arabic — which then flipped the reply
    // language and the spoken voice for the rest of the turn.
    params.set('lang', i18n.language || 'en')
    const wsUrl = `${proto}//${window.location.host}/api/v1/voice/${sid}?${params.toString()}`
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      voiceStore.setState('listening')
      // Apply the customer's saved voice from the first reply onward.
      try { ws.send(JSON.stringify({ type: 'set_voice', voice: useVoiceStore.getState().voiceId })) } catch { /* noop */ }
      startRecording(
        (pcm16) => { if (ws.readyState === WebSocket.OPEN) ws.send(pcm16) },
        (amps) => voiceStore.setAmplitudes(amps),
      )
        .then((handle) => { recordingRef.current = handle })
        .catch(() => voiceStore.setState('idle'))
    }

    ws.onmessage = (event) => {
      let msg: VoiceWsMessage
      try { msg = JSON.parse(event.data as string) as VoiceWsMessage } catch { return }

      if (msg.type === 'transcript') {
        voiceStore.upsertTurn(msg.role ?? 'assistant', msg.transcript ?? '', msg.partial)
      } else if (msg.type === 'response_audio_chunk') {
        if (interruptedRef.current) return // chunk from a cancelled reply — drop it
        if (!playerRef.current) playerRef.current = new PCMPlayer()
        const bytes = Uint8Array.from(atob(msg.audio ?? ''), (c) => c.charCodeAt(0))
        playerRef.current.feed(bytes.buffer)
      } else if (msg.type === 'audio_interrupted') {
        // User barged in — stop the assistant immediately and drop in-flight audio.
        interruptedRef.current = true
        playerRef.current?.destroy()
        playerRef.current = null
        voiceStore.setState('listening')
      } else if (msg.type === 'state_change' && msg.state) {
        if (msg.state === 'speaking') {
          // A new reply is starting — hard-stop anything still playing so two
          // replies can never overlap, then accept fresh audio.
          playerRef.current?.destroy()
          playerRef.current = null
          interruptedRef.current = false
        }
        voiceStore.setState(msg.state)
      } else if (msg.type === 'error') {
        voiceStore.setState('idle')
      }
    }

    ws.onclose = () => voiceStore.setState('idle')
    ws.onerror = () => voiceStore.setState('idle')
  }, [voiceStore])

  const endSession = useCallback(() => {
    tokenRef.current++ // invalidate any in-flight startSession
    interruptedRef.current = false
    recordingRef.current?.stop()
    recordingRef.current = null
    playerRef.current?.destroy()
    playerRef.current = null

    const ws = wsRef.current
    wsRef.current = null
    if (ws) {
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'end' })) } catch { /* noop */ }
        try { ws.close() } catch { /* noop */ }
      } else if (ws.readyState === WebSocket.CONNECTING) {
        // Can't send/close cleanly yet — close once it opens to avoid InvalidStateError.
        ws.onopen = () => { try { ws.close() } catch { /* noop */ } }
        ws.onmessage = null
        ws.onerror = null
      } else {
        try { ws.close() } catch { /* noop */ }
      }
    }
    voiceStore.resetSession()
  }, [voiceStore])

  const toggleMute = useCallback(() => {
    const muted = !voiceStore.isMuted
    recordingRef.current?.stream.getAudioTracks().forEach((t) => { t.enabled = !muted })
    voiceStore.setMuted(muted)
  }, [voiceStore])

  const setVoice = useCallback((voiceId: string) => {
    voiceStore.setVoiceId(voiceId)
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'set_voice', voice: voiceId })) } catch { /* noop */ }
    }
  }, [voiceStore])

  return {
    state: voiceStore.state,
    amplitudes: voiceStore.amplitudes,
    turns: voiceStore.turns,
    isMuted: voiceStore.isMuted,
    voiceId: voiceStore.voiceId,
    startSession,
    endSession,
    toggleMute,
    setVoice,
  }
}
